/* Low Visual motion and small store behaviours. No dependencies. */
(() => {
  const calm = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const design = window.Shopify && Shopify.designMode;
  const body = document.body;
  const rootEl = document.documentElement;
  if (!calm && !design) rootEl.classList.add('lv-ready');

  /* Header overlay needs the real header height */
  const head = document.querySelector('.section-header');
  const measure = () => head && rootEl.style.setProperty('--lv-head', head.offsetHeight + 'px');
  measure();
  addEventListener('resize', measure, { passive: true });

  /* One scroll loop for the header state, hero drift and field parallax */
  const hero = document.querySelector('.lv-hero');
  const drift = [];
  let queued = false;
  const frame = () => {
    queued = false;
    const y = scrollY;
    rootEl.classList.toggle('lv-scrolled', y > 24);
    if (calm) return;
    if (hero && y < hero.offsetHeight) hero.style.setProperty('--lv-p', (y / hero.offsetHeight).toFixed(3));
    drift.forEach((el) => {
      if (!el.lvLive) return;
      const box = el.parentElement.getBoundingClientRect();
      const offset = (box.top + box.height / 2 - innerHeight / 2) / innerHeight;
      el.style.transform = `translate3d(0, ${(offset * -5).toFixed(2)}%, 0) scale(1.12)`;
    });
  };
  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(frame);
  };
  addEventListener('scroll', onScroll, { passive: true });
  frame();

  /* Reveal each block once as it arrives */
  if (rootEl.classList.contains('lv-ready')) {
    const reveal = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          reveal.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    document.querySelectorAll('[data-lv-in]').forEach((el) => reveal.observe(el));
    const foot = document.querySelector('.footer');
    if (foot) reveal.observe(foot);
  }

  /* Field photos drift a little on desktop only */
  if (fine && !calm) {
    const inView = new IntersectionObserver((entries) => {
      entries.forEach((entry) => (entry.target.lvLive = entry.isIntersecting));
      onScroll();
    });
    document.querySelectorAll('[data-lv-drift]').forEach((el) => {
      drift.push(el);
      inView.observe(el);
    });
  }

  /* Footer wordmark follows the pointer like a flashlight */
  const footer = document.querySelector('.footer');
  if (footer && fine && !calm) {
    footer.addEventListener('pointermove', (event) => {
      const box = footer.getBoundingClientRect();
      footer.classList.add('lv-torch');
      footer.style.setProperty('--lv-x', ((event.clientX - box.left) / box.width) * 100 + '%');
    });
    footer.addEventListener('pointerleave', () => footer.style.setProperty('--lv-x', '-30%'));
  }

  /* Inside section: the color floods out from the tap */
  const initInside = (section) => {
    const flood = section.querySelector('.lv-inside__flood');
    const picks = [...section.querySelectorAll('.lv-pick')];
    const slides = [...section.querySelectorAll('[data-lv-slide]')];
    const link = section.querySelector('.lv-inside__link');
    const paint = (bg, fg) => {
      section.style.setProperty('--lv-bg', bg);
      section.style.setProperty('--lv-fg', fg);
    };
    picks.forEach((pick, index) => {
      pick.addEventListener('click', (event) => {
        if (pick.getAttribute('aria-pressed') === 'true') return;
        picks.forEach((other) => other.setAttribute('aria-pressed', String(other === pick)));
        slides.forEach((slide) => slide.classList.toggle('is-on', slide.dataset.lvSlide === String(index)));
        if (link) {
          link.href = pick.dataset.url;
          link.querySelector('span').textContent = pick.dataset.title;
        }
        const { bg, fg } = pick.dataset;
        section.style.setProperty('--lv-fg', fg);
        if (calm || !flood.animate) return paint(bg, fg);
        const box = section.getBoundingClientRect();
        const pickBox = pick.getBoundingClientRect();
        const x = (event.clientX || pickBox.left + pickBox.width / 2) - box.left;
        const y = (event.clientY || pickBox.top + pickBox.height / 2) - box.top;
        const reach = Math.hypot(Math.max(x, box.width - x), Math.max(y, box.height - y));
        flood.style.background = bg;
        flood.style.visibility = 'visible';
        flood
          .animate(
            [{ clipPath: `circle(0px at ${x}px ${y}px)` }, { clipPath: `circle(${reach}px at ${x}px ${y}px)` }],
            { duration: 750, easing: 'cubic-bezier(.2,.7,.2,1)' }
          )
          .finished.then(() => {
            paint(bg, fg);
            flood.style.visibility = 'hidden';
          })
          .catch(() => paint(bg, fg));
      });
    });
  };
  document.querySelectorAll('.lv-inside').forEach(initInside);
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.lv-inside');
    if (section) initInside(section);
  });

  /* Product page: jump the gallery between the outside and the liner */
  const liner = document.querySelector('[data-lv-inside-pos]');
  const gallery = document.querySelector('media-gallery');
  if (liner && gallery) {
    customElements.whenDefined('media-gallery').then(() => {
      const media = [...gallery.querySelectorAll('.product__media-item[data-media-id]')];
      const outsideId = media[0] && media[0].dataset.mediaId;
      const insideEl = media[Number(liner.dataset.lvInsidePos) - 1];
      if (!outsideId || !insideEl) return;
      const wrap = gallery.closest('.product__media-wrapper') || gallery;
      const view = document.createElement('div');
      view.className = 'lv-view';
      view.setAttribute('role', 'group');
      view.setAttribute('aria-label', 'Photo view');
      const choices = [
        ['Outside', outsideId],
        ['Inside', insideEl.dataset.mediaId],
      ].map(([label, id], index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        button.setAttribute('aria-pressed', String(index === 0));
        button.addEventListener('click', () => {
          choices.forEach((choice) => choice.setAttribute('aria-pressed', String(choice === button)));
          gallery.setActiveMedia(id, true);
        });
        view.append(button);
        return button;
      });
      wrap.append(view);
    });
  }

  /* Phones: keep Add to cart in reach whenever the real button is off screen */
  const submit = document.querySelector('.product-form__submit');
  const auction = document.title.toUpperCase().includes('AUCTION');
  if (submit && !auction && 'IntersectionObserver' in window) {
    const bar = document.createElement('div');
    bar.className = 'lv-buybar';
    const price = document.createElement('p');
    const buy = document.createElement('button');
    buy.type = 'button';
    buy.className = 'button button--primary';
    buy.textContent = 'Add to cart';
    buy.addEventListener('click', () => submit.click());
    bar.append(price, buy);
    body.append(bar);
    if (innerWidth < 750) body.style.paddingBottom = bar.offsetHeight + 'px';
    new IntersectionObserver(
      ([entry]) => {
        const readable = document.querySelector('.product .price-item--last, .product .price-item--regular');
        price.textContent = readable ? readable.textContent.trim().replace(/\.00(?=\s|$)/, '') : '';
        const usable = submit.offsetParent !== null && !submit.disabled;
        bar.classList.toggle('is-on', usable && !entry.isIntersecting);
      },
      { threshold: 0 }
    ).observe(submit);
  }
})();
